import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PantryCategory, PantryItem, PantryLocation, IngredientUnit } from '@/types';
import {
  getPantryItems,
  savePantryItem,
  deletePantryItem,
  clearPantryItems,
} from '@/lib/data-layer';
import { generateId } from '@/lib/utils';

interface PantryItemInput {
  id?: string;
  ingredientName: string;
  amount: number;
  unit: IngredientUnit;
  category?: PantryCategory;
  location?: PantryLocation;
  lowStockThreshold?: number;
  expiresAt?: string;
  notes?: string;
}

interface PantryState {
  items: PantryItem[];
  isLoading: boolean;
  error: string | null;
  loadItems: () => Promise<void>;
  upsertItem: (input: PantryItemInput) => Promise<PantryItem>;
  removeItem: (id: string) => Promise<void>;
  adjustAmount: (id: string, delta: number) => Promise<void>;
  clearItems: () => Promise<void>;
}

function normalizeString(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function sortByName(items: PantryItem[]): PantryItem[] {
  return [...items].sort((a, b) => a.ingredientName.localeCompare(b.ingredientName));
}

export const usePantryStore = create<PantryState>()(
  devtools(
    (set, get) => ({
      items: [],
      isLoading: false,
      error: null,

      loadItems: async () => {
        set({ isLoading: true, error: null });
        try {
          const items = await getPantryItems();
          set({ items: sortByName(items), isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load pantry',
          });
        }
      },

      upsertItem: async (input) => {
        const now = new Date().toISOString();
        const ingredientName = normalizeString(input.ingredientName);
        const existing = input.id ? get().items.find((item) => item.id === input.id) : undefined;

        const nextItem: PantryItem = {
          id: existing?.id ?? input.id ?? generateId(),
          ingredientName,
          amount: Math.max(0, Number(input.amount) || 0),
          unit: input.unit ?? '',
          category: input.category ?? existing?.category ?? 'other',
          location: input.location ?? existing?.location ?? 'pantry',
          lowStockThreshold: Math.max(0, Number(input.lowStockThreshold ?? existing?.lowStockThreshold ?? 1)),
          expiresAt: input.expiresAt?.trim() || existing?.expiresAt || undefined,
          notes: input.notes?.trim() || existing?.notes || undefined,
          createdAt: existing?.createdAt ?? now,
          updatedAt: now,
        };

        await savePantryItem(nextItem);

        set((state) => {
          const nextItems = state.items.filter((item) => item.id !== nextItem.id);
          nextItems.push(nextItem);
          return { items: sortByName(nextItems) };
        });

        return nextItem;
      },

      removeItem: async (id) => {
        await deletePantryItem(id);
        set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
      },

      adjustAmount: async (id, delta) => {
        const existing = get().items.find((item) => item.id === id);
        if (!existing) return;
        const nextAmount = Math.max(0, Math.round((existing.amount + delta) * 100) / 100);
        const updated: PantryItem = {
          ...existing,
          amount: nextAmount,
          updatedAt: new Date().toISOString(),
        };
        await savePantryItem(updated);
        set((state) => ({
          items: sortByName(state.items.map((item) => (item.id === id ? updated : item))),
        }));
      },

      clearItems: async () => {
        await clearPantryItems();
        set({ items: [] });
      },
    }),
    { name: 'pantry-store' }
  )
);
